import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { PAYMENT_SIMULATION_MS, TICKET_PRICE } from '../../../core/constants/lottery';
import { PAYMENT_STATUS, TEST_CARD } from '../../../core/constants/payment';
import { ROUTES } from '../../../core/constants/routes';
import { delay } from '../../../core/utils/async';
import { formatCurrency, formatTime } from '../../../core/utils/format';
import { useTickets } from '../../../global/contexts/lottery/tickets';
import { useCart } from '../contexts/cart';
import {
    formatCardNumber,
    formatExpiry,
    maskCardNumber,
    normalizeCardNumber,
    normalizeCvc,
    normalizeExpiry,
    normalizeHolder,
    validatePayment,
} from '../utils/card';

import '../styles/checkout.css';

const EMPTY_FORM = { holder: '', number: '', expiry: '', cvc: '' };

function Field({ id, label, error, children, wide = false }) {
    return (
        <div className={`checkout__field ${wide ? 'checkout__field--wide' : ''}`}>
            <label className="checkout__label" htmlFor={id}>{label}</label>
            {children}
            {error && <span className="checkout__error">{error}</span>}
        </div>
    );
}

function Receipt({ receipt, playsAt, onClose }) {
    const total = receipt.tickets.length * TICKET_PRICE;

    return (
        <div className="checkout__receipt">
            <span className="checkout__check" aria-hidden="true">✓</span>
            <h2 className="checkout__title">¡Pago aprobado!</h2>
            <p className="checkout__reference">
                Referencia <strong>{receipt.paymentReference}</strong>
            </p>

            <ul className="checkout__numbers">
                {receipt.tickets.map(ticket => (
                    <li className="checkout__number" key={ticket.id}>{ticket.number}</li>
                ))}
            </ul>

            <dl className="checkout__summary">
                <div className="checkout__summary-row">
                    <dt>Números</dt>
                    <dd>{receipt.tickets.length}</dd>
                </div>
                <div className="checkout__summary-row">
                    <dt>Total pagado</dt>
                    <dd>{formatCurrency(total)}</dd>
                </div>
                <div className="checkout__summary-row">
                    <dt>Juegan a las</dt>
                    <dd>{formatTime(playsAt)}</dd>
                </div>
            </dl>

            <div className="checkout__actions">
                <Link className="button button--primary" to={ROUTES.TICKETS} onClick={onClose}>
                    Ver mis números
                </Link>
                <button type="button" className="button button--ghost" onClick={onClose}>
                    Seguir comprando
                </button>
            </div>
        </div>
    );
}

// The store mounts this only while the dialog is open, so closing it discards
// the form and the receipt — no reset effect needed.
export default function CheckoutForm({ numbers, playsAt, onClose }) {
    const { purchase } = useTickets();
    const { clear } = useCart();

    const [form, setForm]       = useState(EMPTY_FORM);
    const [errors, setErrors]   = useState({});
    const [status, setStatus]   = useState(PAYMENT_STATUS.IDLE);
    const [receipt, setReceipt] = useState(null);

    const isProcessing = status === PAYMENT_STATUS.PROCESSING;

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !isProcessing) onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isProcessing, onClose]);

    const update = (key, value) => {
        setForm(current => ({ ...current, [key]: value }));
        setErrors(current => ({ ...current, [key]: undefined }));
    };

    const fillTestCard = () => {
        setForm(TEST_CARD);
        setErrors({});
    };

    const total = numbers.length * TICKET_PRICE;

    const handleSubmit = async (event) => {
        event.preventDefault();
        const { errors: found, isValid } = validatePayment(form);
        setErrors(found);
        if (!isValid || !numbers.length) return;

        setStatus(PAYMENT_STATUS.PROCESSING);
        await delay(PAYMENT_SIMULATION_MS);

        // No unmount guard: closing the dialog is blocked while processing, and
        // if the visitor navigates away the purchase must still register — the
        // payment already "went through". setState on an unmounted tree is a
        // no-op in React 18+.
        const result = purchase(numbers);
        setReceipt(result);
        setStatus(PAYMENT_STATUS.SUCCESS);
        clear();
        toast.success(`Pago aprobado · ${result.paymentReference}`);
    };

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget && !isProcessing) onClose();
    };

    return (
        <div className="checkout" onClick={handleOverlayClick}>
            <div className="checkout__dialog panel" role="dialog" aria-modal="true" aria-label="Pago">
                {receipt ? (
                    <Receipt receipt={receipt} playsAt={playsAt} onClose={onClose} />
                ) : (
                    <form className="checkout__form" onSubmit={handleSubmit} noValidate>
                        <header className="checkout__head">
                            <div>
                                <h2 className="checkout__title">Pago seguro</h2>
                                <p className="checkout__lead">
                                    {numbers.length} {numbers.length === 1 ? 'número' : 'números'} ·
                                    juegan a las {formatTime(playsAt)}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="checkout__close"
                                onClick={onClose}
                                disabled={isProcessing}
                                aria-label="Cerrar"
                            >
                                ✕
                            </button>
                        </header>

                        <ul className="checkout__numbers">
                            {numbers.map(number => (
                                <li className="checkout__number" key={number}>{number}</li>
                            ))}
                        </ul>

                        <div className="checkout__grid">
                            <Field id="holder" label="Nombre en la tarjeta" error={errors.holder} wide>
                                <input
                                    id="holder"
                                    className="checkout__input"
                                    value={form.holder}
                                    onChange={e => update('holder', normalizeHolder(e.target.value))}
                                    placeholder="JUAN PEREZ"
                                    autoComplete="cc-name"
                                    disabled={isProcessing}
                                    autoFocus
                                />
                            </Field>

                            <Field id="number" label="Número de tarjeta" error={errors.number} wide>
                                <input
                                    id="number"
                                    className="checkout__input checkout__input--mono"
                                    value={formatCardNumber(form.number)}
                                    onChange={e => update('number', normalizeCardNumber(e.target.value))}
                                    placeholder="4242 4242 4242 4242"
                                    inputMode="numeric"
                                    autoComplete="cc-number"
                                    disabled={isProcessing}
                                />
                            </Field>

                            <Field id="expiry" label="Vence" error={errors.expiry}>
                                <input
                                    id="expiry"
                                    className="checkout__input checkout__input--mono"
                                    value={formatExpiry(form.expiry)}
                                    onChange={e => update('expiry', normalizeExpiry(e.target.value))}
                                    placeholder="MM/AA"
                                    inputMode="numeric"
                                    autoComplete="cc-exp"
                                    disabled={isProcessing}
                                />
                            </Field>

                            <Field id="cvc" label="CVC" error={errors.cvc}>
                                <input
                                    id="cvc"
                                    className="checkout__input checkout__input--mono"
                                    value={form.cvc}
                                    onChange={e => update('cvc', normalizeCvc(e.target.value))}
                                    placeholder="123"
                                    inputMode="numeric"
                                    autoComplete="cc-csc"
                                    disabled={isProcessing}
                                />
                            </Field>
                        </div>

                        <button
                            type="button"
                            className="checkout__testcard"
                            onClick={fillTestCard}
                            disabled={isProcessing}
                        >
                            Usar tarjeta de prueba {maskCardNumber(TEST_CARD.number)}
                        </button>

                        <footer className="checkout__foot">
                            <div className="checkout__total">
                                <span className="checkout__total-label">Total a pagar</span>
                                <strong className="checkout__total-value">{formatCurrency(total)}</strong>
                            </div>
                            <button
                                type="submit"
                                className="button button--primary checkout__submit"
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Procesando…' : `Pagar ${formatCurrency(total)}`}
                            </button>
                        </footer>

                        <p className="checkout__disclaimer">
                            Demo: no se procesa ningún cobro real ni se envían datos a ninguna pasarela.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}
